import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";

function AccessCoverPriceTable() {
    const [accessCovers, setAccessCovers] = useState([]);
    const [filteredAccessCovers, setFilteredAccessCovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedCover, setSelectedCover] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        size: "",
        coverType: "",
        price: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchAccessCovers();
    }, []);

    // Apply filters whenever accessCovers or filter settings change
    useEffect(() => {
        applyFilters();
    }, [accessCovers, filterText, filterField]);

    const fetchAccessCovers = async () => {
        setLoading(true);
        try {
            console.log("Fetching access covers...");

            const response = await apiClient.get("/api/PumpStationPriceTables/AccessCoverPrices");

            console.log("Response received successfully!");
            console.log("Data type:", typeof response.data);
            console.log("Is array:", Array.isArray(response.data));

            if (Array.isArray(response.data)) {
                setAccessCovers(response.data);
                console.log(`Successfully loaded ${response.data.length} access covers`);
            } else {
                console.warn("Response is not an array:", response.data);
                setAccessCovers([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching access covers:", err);
            setError(`Failed to load data: ${err.message}`);
            setAccessCovers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (cover = null) => {
        if (cover) {
            setFormData({
                id: cover.id || cover.Id || 0,
                size: cover.size || cover.Size || "",
                coverType: cover.coverType || cover.CoverType || "",
                price: cover.price || cover.Price || 0,
            });
            setSelectedCover(cover);
        } else {
            setFormData({
                id: 0,
                size: "",
                coverType: "",
                price: 0,
            });
            setSelectedCover(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (cover) => {
        setSelectedCover(cover);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedCover(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "price" ? parseFloat(value) || 0 : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                Size: formData.size,
                CoverType: formData.coverType,
                Price: formData.price,
                Quotations: [],
            };

            let response;
            if (selectedCover) {
                // Update existing record
                response = await apiClient.put(
                    `/api/PumpStationPriceTables/AccessCoverPrices/${payload.Id}`,
                    payload
                );
                setSnackbar({
                    open: true,
                    message: "Access cover updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/PumpStationPriceTables/AccessCoverPrices", payload);
                setSnackbar({
                    open: true,
                    message: "Access cover created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchAccessCovers();
        } catch (err) {
            console.error("Error saving access cover:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/AccessCoverPrices/${selectedCover.id || selectedCover.Id}`
            );
            setSnackbar({
                open: true,
                message: "Access cover deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchAccessCovers();
        } catch (err) {
            console.error("Error deleting access cover:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            // Handle the ID/modelNo column specially
            if (orderBy === "modelNo") {
                const idA = parseInt(a.id || a.Id || 0);
                const idB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? idA - idB : idB - idA;
            }

            // For any other column, get the property with both camelCase and PascalCase checks
            let valueA, valueB;

            // Try camelCase first, then PascalCase if not found
            if (orderBy === "size") {
                valueA = a.size !== undefined ? a.size : a.Size;
                valueB = b.size !== undefined ? b.size : b.Size;
            } else if (orderBy === "coverType") {
                valueA = a.coverType !== undefined ? a.coverType : a.CoverType;
                valueB = b.coverType !== undefined ? b.coverType : b.CoverType;
            } else if (orderBy === "price") {
                valueA = a.price !== undefined ? a.price : a.Price;
                valueB = b.price !== undefined ? b.price : b.Price;

                // Convert to numbers for price comparison
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            // For non-numeric values, convert to strings and compare
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            // For numeric values
            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredAccessCovers(accessCovers);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = accessCovers.filter((cover) => {
            // Format model number for filtering (AC-001, AC-002, etc.)
            const modelNo = `AC-${(cover.id || cover.Id || 0).toString().padStart(3, "0")}`;

            // Handle both camelCase and PascalCase property names from API
            const size = cover.size || cover.Size || "";
            const coverType = cover.coverType || cover.CoverType || "";
            const price = cover.price || cover.Price || 0;

            if (filterField === "all") {
                return (
                    modelNo.toLowerCase().includes(searchTerm) ||
                    String(cover.id || cover.Id || "").includes(searchTerm) ||
                    size.toLowerCase().includes(searchTerm) ||
                    coverType.toLowerCase().includes(searchTerm) ||
                    String(price).includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "modelNo") return modelNo.toLowerCase().includes(searchTerm);
            if (filterField === "id") return String(cover.id || cover.Id || "").includes(searchTerm);
            if (filterField === "size") return size.toLowerCase().includes(searchTerm);
            if (filterField === "coverType") return coverType.toLowerCase().includes(searchTerm);
            if (filterField === "price") return String(price).includes(searchTerm);

            return false;
        });

        setFilteredAccessCovers(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredAccessCovers);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Access Cover Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "modelNo", label: "ID" },
                                        { id: "size", label: "Size" },
                                        { id: "coverType", label: "Type" },
                                        { id: "price", label: "Price" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "modelNo"
                                    ? "ID"
                                    : filterField === "id"
                                    ? "ID"
                                    : filterField === "size"
                                    ? "Size"
                                    : filterField === "coverType"
                                    ? "Type"
                                    : filterField === "price"
                                    ? "Price"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Item
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="access cover price table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "modelNo"}
                                                direction={orderBy === "modelNo" ? order : "asc"}
                                                onClick={() => handleRequestSort("modelNo")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "size"}
                                                direction={orderBy === "size" ? order : "asc"}
                                                onClick={() => handleRequestSort("size")}
                                            >
                                                <strong>Size</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "coverType"}
                                                direction={orderBy === "coverType" ? order : "asc"}
                                                onClick={() => handleRequestSort("coverType")}
                                            >
                                                <strong>Type</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "price"}
                                                direction={orderBy === "price" ? order : "asc"}
                                                onClick={() => handleRequestSort("price")}
                                            >
                                                <strong>Price</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first access cover."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((cover) => (
                                            <TableRow key={cover.id || cover.Id} hover>
                                                <TableCell>
                                                    AC-
                                                    {(cover.id || cover.Id).toString().padStart(3, "0")}
                                                </TableCell>
                                                <TableCell>{cover.size || cover.Size}</TableCell>
                                                <TableCell>{cover.coverType || cover.CoverType}</TableCell>
                                                <TableCell>
                                                    $
                                                    {typeof (cover.price || cover.Price) === "number"
                                                        ? (cover.price || cover.Price).toFixed(2)
                                                        : "0.00"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(cover)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(cover)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${accessCovers.length} items`
                                    : `Total: ${accessCovers.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedCover ? "Edit Access Cover" : "Add New Access Cover"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Size"
                                    name="size"
                                    value={formData.size}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., 600R"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Cover Type"
                                    name="coverType"
                                    value={formData.coverType}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., Class 5, Solid Top, Cast Iron Access Cover"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Price"
                                    name="price"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.price}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedCover ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this access cover? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AccessCoverPriceTable;
